import { ServiceModalityE } from '@/types/cyclone/models';
import React, { FunctionComponent, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts';
import { useNavigate } from 'react-router';
import classNames from 'classnames';
import { ResponsiveContainer, Row } from '@/components';
import ServiceAvailability from './components/Steps/ServiceAvailability';
import ServiceType from './components/Steps/ServiceType';
import ServiceModality from './components/Steps/ServiceModality';
import { ServiceForm } from './components';
import { motion, AnimatePresence } from 'framer-motion';
import { useService } from '../../context';
import { ChevronRight } from 'lucide-react';

enum Steps {
  SERVICE_MODALITY = 1,
  SERVICE_TYPE = 2,
  SERVICE_AVAILABILITY = 3,
  SERVICE_FORM = 4
}

export const NewService: FunctionComponent = () => {
  const { modality, setIsOnDemand, setModality, isOnDemand, isOwnAvailability, setIsOwnAvailability } =
    useService();
  const [currentStep, setCurrentStep] = React.useState<Steps>(Steps.SERVICE_MODALITY);
  const pathname = window.location.pathname;
  const isSetup = pathname.includes('setup');

  const isFirstRender = useRef(true);

  const navigate = useNavigate();
  const { session } = useAuth();
  const isFirstService = session?.vendor?.vendor_activity_tracking?.first_service_at === null;

  const stepVariants = {
    hidden: {
      opacity: 0,
      y: 10,
      transition: { duration: 0.3 }
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: 'easeOut' }
    },
    exit: {
      opacity: 0,
      y: -10,
      transition: { duration: 0.2 }
    }
  };

  const goToNextStep = (nextStep: Steps) => {
    setTimeout(() => {
      setCurrentStep(nextStep);
    }, 300);
  };

  // vanebeauty
  const isBeautyCategory = session?.is_beauty_category;
  const ffList = [
    1, 143191, 13068, 13010, 12806, 3821, 13173, 13213, 13292, 13177, 13378, 13177, 13503, 13598, 10488,
    13224, 13647, 13866, 13888, 13828, 4564, 14083, 14284, 14546, 15252
  ];
  const featureFlagOwnAvailability = ffList.includes(session?.vendor?.id as number);

  useEffect(() => {
    if (isBeautyCategory) {
      setIsOnDemand(true);
    }
  }, [isBeautyCategory]);

  const handleModalityChange = (modalityValue: ServiceModalityE) => {
    setModality(modalityValue);
    if (isBeautyCategory && featureFlagOwnAvailability) {
      goToNextStep(Steps.SERVICE_AVAILABILITY);
    } else if (isBeautyCategory && !featureFlagOwnAvailability) {
      goToNextStep(Steps.SERVICE_FORM);
    } else {
      goToNextStep(Steps.SERVICE_TYPE);
    }
  };

  const handleServiceTypeChange = (isOnDemandValue: boolean) => {
    setIsOnDemand(isOnDemandValue);
    if (isOnDemandValue && featureFlagOwnAvailability) {
      goToNextStep(Steps.SERVICE_AVAILABILITY);
    } else {
      goToNextStep(Steps.SERVICE_FORM);
    }
  };

  const handleAvailabilityChange = (availabilityValue: boolean) => {
    setIsOwnAvailability(availabilityValue);
    goToNextStep(Steps.SERVICE_FORM);
  };

  const renderCurrentStep = () => {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={isFirstRender.current ? false : 'hidden'}
          animate="visible"
          exit="exit"
          variants={stepVariants}
        >
          {(() => {
            switch (currentStep) {
              case Steps.SERVICE_TYPE:
                return (
                  <ServiceType isSetup={isSetup} value={isOnDemand} onChange={handleServiceTypeChange} />
                );
              case Steps.SERVICE_MODALITY:
                return <ServiceModality isSetup={isSetup} value={modality} onChange={handleModalityChange} />;
              case Steps.SERVICE_AVAILABILITY:
                return (
                  <ServiceAvailability
                    isSetup={isSetup}
                    value={isOwnAvailability}
                    onChange={handleAvailabilityChange}
                  />
                );
              case Steps.SERVICE_FORM:
                return <ServiceForm isSetup={isSetup} />;
              default:
                return null;
            }
          })()}
        </motion.div>
      </AnimatePresence>
    );
  };

  if (isSetup) return renderCurrentStep();

  return (
    <>
      <div className="w-full">
        <ResponsiveContainer>
          <Row align="center" justify="space-between" className="border-b py-4">
            <Row align="center" className="gap-2 sm:gap-4 text-xl leading-none sm:text-3xl sm:leading-none">
              <p
                className={classNames({ 'cursor-pointer': !isFirstService })}
                onClick={() => (!isFirstService ? navigate('/servicios') : null)}
              >
                Servicios
              </p>
              <ChevronRight size={20} />
              <p>Nuevo servicio</p>
            </Row>
            {!isFirstService && (
              <span
                onClick={() => navigate('/servicios')}
                className="w-auto flex gap-4 cursor-pointer items-center justify-center font-medium sm:w-[180px] px-8 rounded-full text-black h-[44px] text-base bg-white border"
              >
                Volver
              </span>
            )}
          </Row>
        </ResponsiveContainer>
      </div>
      <div className="container p-4 max-w-2xl">{renderCurrentStep()}</div>
    </>
  );
};
